import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { Permission, SessionView, SetupItem, SetupItemKey } from '@wodi/shared';
import { Button, EmptyState, LoadingBlock, Notice, PageHeader, Tag } from '@wodi/ui';

import { ApiError } from '../api/client.ts';
import { setupStatusQuery } from '../api/home.ts';
import { can, sessionQuery } from '../api/session.ts';
import styles from '../home/home.module.css';
import { screen } from '../layout/screen.tsx';
import { SETUP_COPY } from '../home/setup-items.ts';
import { RoleUpdatesNotice } from '../settings/role-updates-notice.tsx';

// The Settings overview (docs/plans/phase1-home-and-demo-data.md §3): every settings area
// this person may open, what it is for, and whether it is set up.

type AreaPath =
  | '/settings/organisation'
  | '/settings/registration'
  | '/settings/users'
  | '/settings/role-updates'
  | '/settings/practitioners'
  | '/settings/service-points'
  | '/settings/appointments'
  | '/settings/numbers'
  | '/settings/services'
  | '/settings/payers'
  | '/settings/terminology'
  | '/settings/imports'
  | '/settings/khis'
  | '/settings/stores'
  | '/settings/wards'
  | '/settings/medicines'
  | '/settings/maternity'
  | '/settings/packages'
  | '/settings/theatres'
  | '/settings/mortuary'
  | '/settings/records'
  | '/settings/billing'
  | '/settings/sha'
  | '/settings/etims'
  | '/settings/clinical-rules'
  | '/settings/lab'
  | '/settings/imaging'
  | '/settings/queue-displays'
  | '/settings/formulary'
  | '/settings/dose-rules'
  | '/settings/pharmacy-items'
  | '/settings/patient-portal'
  | '/settings/operations-lists'
  | '/settings/housekeeping'
  | '/settings/sensitivity-labels'
  | '/settings/audit-log'
  | '/settings/break-glass'
  | '/settings/retention';

interface Area {
  to: AreaPath;
  label: string;
  description: string;
  /** The area opens for anyone holding at least one of these. */
  permissions: readonly Permission[];
  /** Checklist items whose state this area shows. */
  status: readonly SetupItemKey[];
}

/** In the order an administrator usually sets a facility up. */
export const SETTINGS_AREAS: readonly Area[] = [
  {
    to: '/settings/organisation',
    label: 'Organisation',
    description:
      'The organisation, its facilities and branches, their MFL codes and levels, and the idle sign-out.',
    permissions: ['admin.organisation.manage', 'admin.facilities.manage'],
    status: [],
  },
  {
    to: '/settings/users',
    label: 'Users',
    description: 'Who can sign in, their roles and the facilities they work at.',
    permissions: ['admin.users.read'],
    status: ['users'],
  },
  // Privacy tooling (docs/plans/golive-privacy-tooling.md).
  {
    to: '/settings/sensitivity-labels',
    label: 'Sensitivity labels',
    description:
      'The labels that keep sensitive visits (such as HIV or mental health care) to the roles cleared for them.',
    permissions: ['sensitivity_labels.manage'],
    status: [],
  },
  {
    to: '/settings/audit-log',
    label: 'Audit log',
    description:
      'Who did what and when, searched by person, action, record and days, and a tamper check of the whole log.',
    permissions: ['audit.read'],
    status: [],
  },
  {
    to: '/settings/break-glass',
    label: 'Break-glass review',
    description:
      'Each time someone opened a sensitive record they aren’t cleared for, with their reason, to review.',
    permissions: ['records.break_glass.review'],
    status: [],
  },
  {
    to: '/settings/retention',
    label: 'Data retention',
    description:
      'How long sessions, sign-in codes, message details and import files are kept before the daily purge. Clinical records are never deleted.',
    permissions: ['privacy.retention.manage'],
    status: [],
  },
  {
    to: '/settings/role-updates',
    label: 'Role updates',
    description:
      'Permissions new releases add to the standard roles, to accept or decline for each role.',
    permissions: ['admin.roles.manage'],
    status: [],
  },
  {
    to: '/settings/practitioners',
    label: 'Practitioners',
    description:
      "Clinicians' cadre, licensing council, licence number and expiry, and when licences show as expiring.",
    permissions: ['practitioners.manage'],
    status: [],
  },
  {
    to: '/settings/registration',
    label: 'Registration',
    description:
      'The data-sharing consent wording read to patients, duplicate checks and the fields registration requires.',
    permissions: ['admin.organisation.manage'],
    status: [],
  },
  {
    to: '/settings/service-points',
    label: 'Service points',
    description: 'The queues patients are checked in to at this facility.',
    permissions: ['admin.facilities.manage'],
    status: ['service_points'],
  },
  {
    to: '/settings/numbers',
    label: 'Patient and visit numbers',
    description: 'How patient and visit numbers look at each facility.',
    permissions: ['admin.facilities.manage'],
    status: ['number_formats'],
  },
  {
    to: '/settings/services',
    label: 'Services and prices',
    description: 'The services and items Wodi charges for, and their prices.',
    permissions: ['catalogue.read'],
    status: ['services', 'prices'],
  },
  {
    to: '/settings/payers',
    label: 'Payers',
    description: 'Cash, SHA, insurers and their schemes, and the price list each uses.',
    permissions: ['catalogue.read'],
    status: ['payers'],
  },
  {
    to: '/settings/stores',
    label: 'Stores and suppliers',
    description: 'Where stock is kept at this facility, and who supplies it.',
    permissions: ['stock.manage'],
    status: ['stores'],
  },
  {
    to: '/settings/wards',
    label: 'Wards and beds',
    description: 'The wards, rooms and beds at this facility, and what a bed day is charged as.',
    permissions: ['inpatient.wards.manage'],
    status: [],
  },
  {
    to: '/settings/maternity',
    label: 'Maternity and MCH',
    description:
      'Danger-sign lists, partograph lines, the immunisation store, vaccines and the schedule. Empty until set.',
    permissions: ['maternity.settings.manage'],
    status: [],
  },
  {
    to: '/settings/medicines',
    label: 'Medicine timing',
    description:
      'How early or late a dose may be given on the wards before the medicines chart flags it.',
    permissions: ['emar.settings.manage'],
    status: [],
  },
  {
    to: '/settings/packages',
    label: 'Packages',
    description:
      'Fixed-price bundles for a procedure or a stay: what each is charged as and what it covers.',
    permissions: ['billing.packages.manage'],
    status: [],
  },
  {
    to: '/settings/theatres',
    label: 'Theatres',
    description:
      'The theatres at this facility, what the theatre fee is charged as, and the safety checklist wording.',
    permissions: ['theatre.settings.manage'],
    status: [],
  },
  {
    // docs/plans/phase3-operations-modules.md §0.1 (ADR 0036).
    to: '/settings/operations-lists',
    label: 'Operations lists',
    description:
      'Incident categories and the harm scale, and the other lists the quality, infection control, maintenance and kitchen work uses.',
    permissions: ['incidents.manage', 'infection.manage', 'assets.manage', 'kitchen.manage'],
    status: [],
  },
  {
    // docs/plans/phase3-kitchen-and-housekeeping.md §2.4.
    to: '/settings/housekeeping',
    label: 'Housekeeping',
    description: 'The areas housekeeping cleans and the times of day each cleaning task is due.',
    permissions: ['housekeeping.manage'],
    status: [],
  },
  {
    to: '/settings/records',
    label: 'Records',
    description:
      'The records-room locations charts are filed on, and how long a chart may be out before it is overdue.',
    permissions: ['records.settings.manage'],
    status: [],
  },
  {
    to: '/settings/mortuary',
    label: 'Mortuary',
    description: 'The cold-room slots and what a day of storage is charged as.',
    permissions: ['mortuary.settings.manage'],
    status: [],
  },
  {
    to: '/settings/billing',
    label: 'Billing',
    description: 'How bills, receipts and cashier shifts work at this facility.',
    permissions: ['billing.settings.manage'],
    status: [],
  },
  {
    to: '/settings/etims',
    label: 'eTIMS',
    description:
      'How tax documents are signed with KRA (OSCU or the VSCU on site), and the documents still waiting on eTIMS.',
    permissions: ['etims.settings.manage', 'etims.documents.sign_again'],
    status: [],
  },
  {
    to: '/settings/appointments',
    label: 'Appointments',
    description:
      'Clinic and practitioner schedules: the days and hours patients can be booked, slot length and how many share a slot.',
    permissions: ['appointments.schedules.manage'],
    status: [],
  },
  {
    to: '/settings/sha',
    label: 'SHA',
    description:
      'The SHA connection, this facility and its practitioners in the national registries, and how claims are sent.',
    permissions: ['sha.settings.manage'],
    status: [],
  },
  {
    to: '/settings/clinical-rules',
    label: 'Clinical rules',
    description:
      'Vital-sign limits and lab ranges, and the alerts they raise, changed with a second approver.',
    permissions: ['clinical_rules.manage', 'clinical_rules.approve'],
    status: [],
  },
  {
    to: '/settings/lab',
    label: 'Lab',
    description:
      'Lab tests, the analytes they measure and their ranges, the lab’s lists and settings, and its analyzers.',
    permissions: ['lab.catalogue.manage', 'lab.settings.manage', 'lab.analyzers.manage'],
    status: [],
  },
  {
    to: '/settings/imaging',
    label: 'Imaging',
    description:
      'The modality, patient preparation and report template of each imaging test in the catalogue.',
    permissions: ['imaging.catalogue.manage'],
    status: [],
  },
  {
    to: '/settings/formulary',
    label: 'Formulary',
    description:
      'The drugs clinicians can prescribe, their ingredients, routes and groups, and how often doses are given.',
    permissions: ['formulary.manage'],
    status: [],
  },
  {
    to: '/settings/dose-rules',
    label: 'Dose limits',
    description:
      'Maximum and weight-based doses the clinical lead enters with a source, approved by a second person.',
    permissions: ['clinical_rules.manage', 'clinical_rules.approve'],
    status: [],
  },
  {
    to: '/settings/pharmacy-items',
    label: 'Pharmacy items',
    description:
      'Which medicines need a prescription at the counter, and the barcodes that find them.',
    permissions: ['pharmacy.supply_class.manage', 'stock.manage'],
    status: [],
  },
  {
    to: '/settings/queue-displays',
    label: 'Queue displays',
    description: 'Waiting-area screens paired to show ticket numbers being called.',
    permissions: ['admin.facilities.manage'],
    status: [],
  },
  {
    to: '/settings/terminology',
    label: 'Terminology',
    description: 'Code systems such as ICD-11 and LOINC used when coding records.',
    permissions: ['terminology.read'],
    status: ['terminology'],
  },
  {
    // docs/plans/phase3-portal-feedback-kiswahili.md §7.2.
    to: '/settings/patient-portal',
    label: 'Patient portal',
    description:
      'Whether patients can sign in to see their records, the paybill and the consent wording.',
    permissions: ['admin.organisation.manage'],
    status: [],
  },
  {
    to: '/settings/imports',
    label: 'Imports',
    description: 'Loading data from files, with a check before anything is saved.',
    permissions: ['imports.read'],
    status: [],
  },
  {
    to: '/settings/khis',
    label: 'KHIS',
    description:
      'The facility’s KHIS organisation unit, and which KHIS data element each count Wodi sends goes to.',
    permissions: ['khis.mapping.manage'],
    status: [],
  },
];

export function settingsAreasFor(session: SessionView): readonly Area[] {
  return SETTINGS_AREAS.filter((area) => area.permissions.some((p) => can(session, p)));
}

function AreaState({ items }: { items: readonly SetupItem[] }) {
  if (items.length === 0) return <span />;
  const done = items.every((i) => i.state === 'done');
  return <Tag state={done ? 'done' : 'pending'}>{done ? 'Done' : 'To do'}</Tag>;
}

export function SettingsOverviewPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const status = useQuery(setupStatusQuery);
  const areas = session === null ? [] : settingsAreasFor(session);

  if (areas.length === 0)
    return (
      <>
        <PageHeader title="Settings" />
        <EmptyState>
          There are no settings you can change. Ask an administrator if you need access.
        </EmptyState>
      </>
    );

  return (
    <div className={screen.page}>
      <PageHeader title="Settings" />
      <RoleUpdatesNotice />
      {status.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void status.refetch()}>
              Try again
            </Button>
          }
        >
          {status.error instanceof ApiError
            ? status.error.message
            : "Whether each area is set up couldn't be checked. The areas still open."}
        </Notice>
      ) : null}
      {status.isPending ? <LoadingBlock label="Checking setup" rows={1} /> : null}
      <div className={screen.panel}>
        <ul className={styles.list} aria-label="Settings areas">
          {areas.map((area) => {
            const items = (status.data?.items ?? []).filter((i) => area.status.includes(i.key));
            return (
              <li key={area.to} className={styles.listRow}>
                <span className={styles.listLabel}>
                  <Link to={area.to}>{area.label}</Link>
                </span>
                <span className={styles.stack}>
                  <span>{area.description}</span>
                  {items.map((item) => (
                    <span key={item.key} className={styles.muted}>
                      {SETUP_COPY[item.key].describe(item)}.
                    </span>
                  ))}
                </span>
                <AreaState items={items} />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
